import React, { useMemo } from 'react';
import { Redirect, Route, Switch, useLocation } from '@react-router';
import { Helmet } from 'react-helmet-async';
import type { FormikProps } from 'formik';
import { ErrorSplash } from '@shared/components/ErrorSplash';
import { useContactCollectorGuestController } from './ContactCollectorGuest.controller';
import type { Contacts, Context, FieldVisibility } from './shared/types';
import type { RoutePath } from '@shared/core';
import { RoutesProvider, useRouterHelper, useTranslation } from '@shared/core';
import { TelemetryProvider } from './ContactCollectorGuest.telemetry';
import PageNotFound from '@shared/components/PageNotFound/PageNotFound';
import { Landing, Form, Address, Confirmation, Done } from './routes';
import type { GetContactCollectionByMagicLinkQuery } from '../../admin/contactCollector/api/ContactCollector.graphql.generated';
import { NoSsr } from '@shared/components/NoSsr';
import { createGlobalStyle } from '@withjoy/joykit';
import { withWindow } from '@shared/utils/withWindow';
// Load Fonts
import '@assets/charm.css';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { contactCollectorGuestRoutePaths } from './contactCollectorGuest.routes';
import { CONTACT_COLLECTOR_UTM } from './contactCollector.constants';

export { contactCollectorGuestRoutePaths };

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
  }
`;

interface ContactCollectorGuestProps
  extends Readonly<{
    magicLinkId: string;
  }> {}

const RoutesContactCollectorGuest: React.FC<{
  data?: GetContactCollectionByMagicLinkQuery;
  formik: FormikProps<Contacts>;
  context: Context;
  setContext: React.Dispatch<React.SetStateAction<Context>>;
  isMutationLoading: boolean;
  hasMutationError: boolean;
  e164PhoneNumber: { [key: number]: string };
  setE164PhoneNumber: React.Dispatch<React.SetStateAction<{ [key: number]: string }>>;
  emailRule: FieldVisibility;
  phoneNumberRule: FieldVisibility;
  mailingAddressRule: FieldVisibility;
  phoneCountryCode: string;
}> = ({
  data,
  formik,
  context,
  setContext,
  isMutationLoading,
  hasMutationError,
  e164PhoneNumber,
  setE164PhoneNumber,
  emailRule,
  phoneNumberRule,
  mailingAddressRule,
  phoneCountryCode,
}) => {
  const routeHelpers = useRouterHelper();
  const location = useLocation();

  const routes: Array<RoutePath<{}>> = [
    {
      path: routeHelpers.buildPath(contactCollectorGuestRoutePaths.landing.path),
      component: () => (
        <Landing
          data={data}
          setContext={setContext}
        />
      ),
    },
    {
      path: routeHelpers.buildPath(contactCollectorGuestRoutePaths.form.path),
      component: () => (
        <Form
          formik={formik}
          context={context}
          isCollectHouseholdEnabled={!!data?.getContactCollectionByMagicLink?.collectHousehold}
          e164PhoneNumber={e164PhoneNumber}
          setE164PhoneNumber={setE164PhoneNumber}
          routeHelpers={routeHelpers}
          emailRule={emailRule}
          phoneNumberRule={phoneNumberRule}
          mailingAddressRule={mailingAddressRule}
          phoneCountryCode={phoneCountryCode}
        />
      ),
    },
    {
      path: routeHelpers.buildPath(contactCollectorGuestRoutePaths.address.path),
      component: () => <Address formik={formik} />,
    },
    {
      path: routeHelpers.buildPath(contactCollectorGuestRoutePaths.confirmation.path),
      component: () => (
        <Confirmation
          formik={formik}
          context={context}
          isMutationLoading={isMutationLoading}
          hasMutationError={hasMutationError}
          emailRule={emailRule}
          phoneNumberRule={phoneNumberRule}
          mailingAddressRule={mailingAddressRule}
        />
      ),
    },
    {
      path: routeHelpers.buildPath(contactCollectorGuestRoutePaths.done.path),
      component: () => (
        <Done
          formik={formik}
          context={context}
        />
      ),
    },
  ];

  return (
    <>
      <GlobalStyle />
      <Switch>
        {routes.map(route => (
          <Route
            key={route.path}
            path={route.path}
            render={route.component}
            exact
          />
        ))}
        <Redirect to={{ pathname: routes[0].path, search: location.search }} />
      </Switch>
    </>
  );
};

const ContactCollectorGuest: React.FC<ContactCollectorGuestProps> = ({ magicLinkId }) => {
  const { tq } = useTranslation('contactCollector');
  const {
    showErrorPage,
    isContactCollectionInactive,
    eventHandle,
    isLoading,
    data,
    noData,
    ogImage,
    ogTitle,
    ogUrl,
    formik,
    context,
    setContext,
    isMutationLoading,
    hasMutationError,
    telemetryData,
    e164PhoneNumber,
    setE164PhoneNumber,
    emailRule,
    phoneNumberRule,
    mailingAddressRule,
    phoneCountryCode,
  } = useContactCollectorGuestController(magicLinkId);

  const pageTitle = tq`Contact Collector`;
  const Head = useMemo(() => {
    return (
      <Helmet title={pageTitle}>
        <meta
          property="og:title"
          content={ogTitle}
        />
        <meta
          property="og:site_name"
          content="Joy"
        />
        <meta
          property="og:url"
          content={ogUrl}
        />
        <meta
          property="og:image"
          content={ogImage}
        />
        <meta
          property="og:type"
          content="website"
        />
      </Helmet>
    );
  }, [ogImage, ogTitle, ogUrl, pageTitle]);

  const errorPageTitle = tq`Hmm… we're having some trouble.`;
  const errorPageSubTitle = tq`This page may have changed or is temporarily unavailable.`;

  if (isLoading)
    return (
      <>
        {Head}
        <JoyLogoLoader loaderKey="contact-collector-guest" />
      </>
    );
  if (showErrorPage) return <ErrorSplash customError={{ title: errorPageTitle, subtitle: errorPageSubTitle }} />;
  if (noData) return <PageNotFound />;
  if (isContactCollectionInactive) {
    if (eventHandle) {
      withWindow(() => {
        window.location.href = window.location.origin + `/${eventHandle}?${CONTACT_COLLECTOR_UTM}`;
      });
      return null;
    } else {
      return <ErrorSplash customError={{ title: errorPageTitle, subtitle: errorPageSubTitle }} />;
    }
  }

  return (
    <NoSsr>
      <TelemetryProvider context={telemetryData}>
        <RoutesProvider appUrl="contact/:magicLinkId">
          {Head}
          <RoutesContactCollectorGuest
            data={data}
            formik={formik}
            context={context}
            setContext={setContext}
            isMutationLoading={isMutationLoading}
            hasMutationError={hasMutationError}
            e164PhoneNumber={e164PhoneNumber}
            setE164PhoneNumber={setE164PhoneNumber}
            emailRule={emailRule}
            phoneNumberRule={phoneNumberRule}
            mailingAddressRule={mailingAddressRule}
            phoneCountryCode={phoneCountryCode}
          />
        </RoutesProvider>
      </TelemetryProvider>
    </NoSsr>
  );
};

export default ContactCollectorGuest;
